import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';

const FadeUp = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut', delay }}>
    {children}
  </motion.div>
);

export default function FAQ() {
  const [siteLang, setSiteLang] = useState('FR');
  const [openFaq, setOpenFaq] = useState(null);
  const [openCat, setOpenCat] = useState(0);
  const navigate = useNavigate();

  const content = {
    FR: {
      title: 'FAQ',
      sub: 'Toutes les réponses à vos questions.',
      categories: [
        {
          title: 'Général',
          faqs: [
            { q: "C'est quoi un composite ?", a: "Un composite (ou comp card) est la carte de visite du mannequin. C'est un document A5 recto/verso qui regroupe vos meilleures photos, vos mensurations et vos coordonnées. C'est le premier document demandé par les agences lors d'un casting." },
            { q: "Compify est-il vraiment gratuit ?", a: "Oui, Compify est 100% gratuit. Le service est financé par des publicités ciblées pertinentes pour les mannequins. Aucune carte bancaire, aucun abonnement." },
            { q: "Faut-il créer un compte ?", a: "Non, vous pouvez créer votre composite immédiatement sans inscription. Créez, personnalisez et téléchargez sans jamais créer de compte." },
            { q: "Compify fonctionne sur mobile ?", a: "Compify est optimisé pour ordinateur pour une meilleure expérience de création. Une version mobile est en cours de développement." },
          ]
        },
        {
          title: 'Photos',
          faqs: [
            { q: "Combien de photos puis-je ajouter ?", a: "Vous pouvez importer jusqu'à 16 photos dans votre galerie, et en placer autant que votre template le permet dans votre composite." },
            { q: "Mes photos sont-elles stockées sur vos serveurs ?", a: "Non, vos photos restent sur votre appareil et ne sont jamais envoyées sur nos serveurs. Toute la création se fait localement dans votre navigateur." },
            { q: "Quelle résolution pour mes photos ?", a: "Pour une impression de qualité, nous recommandons des photos d'au moins 1000px de large. Compify vous avertit automatiquement si une photo est de résolution insuffisante." },
            { q: "Quels formats de photos sont acceptés ?", a: "Compify accepte tous les formats courants : JPG, PNG, WEBP et HEIC." },
          ]
        },
        {
          title: 'Export & Impression',
          faqs: [
            { q: "Quel format de sortie ?", a: "PDF haute résolution au format A5 (148x210mm), idéal pour l'impression et l'envoi numérique." },
            { q: "Le composite est-il aux normes agences ?", a: "Oui, le format A5 recto/verso est le standard international utilisé par les agences de mannequinat." },
            { q: "Où imprimer mon composite ?", a: "Nous recommandons d'imprimer sur papier couché 350g minimum. Des imprimeurs en ligne comme Vistaprint ou Pixartprinting proposent ce format à des prix compétitifs." },
            { q: "Puis-je modifier mon composite après téléchargement ?", a: "Oui, vous pouvez revenir sur l'éditeur à tout moment et retélécharger votre composite mis à jour." },
          ]
        },
        {
          title: 'Plan Agence',
          faqs: [
            { q: "À qui s'adresse le plan Agence ?", a: "Le plan Agence est conçu pour les agences de mannequinat, les écoles de mode et les professionnels qui gèrent plusieurs mannequins." },
            { q: "Qu'est-ce que le logo agence personnalisé ?", a: "Avec le plan Agence, votre logo remplace le watermark Compify dans le composite. Chaque composite créé par vos mannequins porte votre identité visuelle." },
            { q: "Comment souscrire au plan Agence ?", a: "Contactez-nous via le formulaire de contact. Notre équipe vous répond sous 24h pour mettre en place votre compte agence." },
            { q: "Y a-t-il un engagement ?", a: "Non, le plan Agence est sans engagement minimum. Vous pouvez annuler à tout moment, mais tout mois entamé reste dû." },
          ]
        },
      ],
      cta: 'CRÉER MON COMPOSITE',
    },
    EN: {
      title: 'FAQ',
      sub: 'All the answers to your questions.',
      categories: [
        {
          title: 'General',
          faqs: [
            { q: "What is a composite card?", a: "A composite card (or comp card) is a model's business card. It's an A5 double-sided document featuring your best photos, measurements and contact details. It's the first document requested by agencies at a casting." },
            { q: "Is Compify really free?", a: "Yes, Compify is 100% free. The service is funded by targeted ads relevant to models. No credit card, no subscription." },
            { q: "Do I need to create an account?", a: "No, you can create your composite immediately without signing up. Create, customize and download without ever creating an account." },
            { q: "Does Compify work on mobile?", a: "Compify is optimized for desktop for the best creation experience. A mobile version is in development." },
          ]
        },
        {
          title: 'Photos',
          faqs: [
            { q: "How many photos can I add?", a: "You can import up to 16 photos into your gallery, and place as many as your template allows in your composite." },
            { q: "Are my photos stored on your servers?", a: "No, your photos stay on your device and are never sent to our servers. All creation happens locally in your browser." },
            { q: "What resolution should my photos be?", a: "For quality printing, we recommend photos of at least 1000px wide. Compify automatically warns you if a photo is too low resolution." },
            { q: "What photo formats are accepted?", a: "Compify accepts all common formats: JPG, PNG, WEBP and HEIC." },
          ]
        },
        {
          title: 'Export & Printing',
          faqs: [
            { q: "What export format?", a: "High-resolution PDF in A5 format (148x210mm), ideal for printing and digital sharing." },
            { q: "Is the composite agency-standard?", a: "Yes, the A5 double-sided format is the international standard used by modeling agencies." },
            { q: "Where can I print my composite?", a: "We recommend printing on coated paper of at least 350g. Online printers like Vistaprint or Pixartprinting offer this format at competitive prices." },
            { q: "Can I edit my composite after downloading?", a: "Yes, you can return to the editor at any time and re-download your updated composite." },
          ]
        },
        {
          title: 'Agency Plan',
          faqs: [
            { q: "Who is the Agency plan for?", a: "The Agency plan is designed for modeling agencies, fashion schools and professionals managing multiple models." },
            { q: "What is the custom agency logo?", a: "With the Agency plan, your logo replaces the Compify watermark in the composite. Every composite created by your models carries your visual identity." },
            { q: "How do I subscribe to the Agency plan?", a: "Contact us via the contact form. Our team will get back to you within 24h to set up your agency account." },
            { q: "Is there a commitment?", a: "No minimum commitment. You can cancel at any time, but any month already started remains due." },
          ]
        },
      ],
      cta: 'CREATE MY COMPOSITE',
    }
  };

  const d = content[siteLang];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>
      <Header siteLang={siteLang} setSiteLang={setSiteLang} />

      {/* HERO */}
      <section style={{ backgroundColor: '#0e0e0e', padding: '80px 24px', textAlign: 'center' }}>
        <FadeUp>
          <p style={{ color: '#FF0066', fontSize: '11px', letterSpacing: '5px', margin: '0 0 16px', fontWeight: '700' }}>COMPIFY</p>
          <h1 style={{ color: '#fff', fontSize: '42px', fontWeight: '900', letterSpacing: '2px', margin: '0 0 20px', textTransform: 'uppercase', lineHeight: 1.1 }}>{d.title}</h1>
          <p style={{ color: '#888', fontSize: '16px', margin: 0 }}>{d.sub}</p>
        </FadeUp>
      </section>

      {/* FAQ */}
      <section style={{ backgroundColor: '#fff', padding: '80px 24px' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', display: 'grid', gridTemplateColumns: '220px 1fr', gap: '64px' }}>

          {/* Catégories */}
          <FadeUp>
            <div style={{ position: 'sticky', top: '100px' }}>
              {d.categories.map((cat, i) => (
                <div key={i} onClick={() => setOpenCat(i)} style={{
                  padding: '14px 16px', cursor: 'pointer', marginBottom: '4px',
                  backgroundColor: openCat === i ? '#FF0066' : 'transparent',
                  borderLeft: `2px solid ${openCat === i ? '#FF0066' : '#eee'}`,
                  transition: 'all 0.2s'
                }}>
                  <span style={{ color: openCat === i ? '#fff' : '#888', fontSize: '12px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                    {cat.title}
                  </span>
                </div>
              ))}
            </div>
          </FadeUp>

          {/* Questions */}
          <div>
            {d.categories[openCat].faqs.map((faq, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <div onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}>
                  <div style={{ padding: '24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ color: '#1A1A1A', fontSize: '15px', fontWeight: '700', margin: 0 }}>{faq.q}</p>
                    <motion.span animate={{ rotate: openFaq === i ? 45 : 0 }} transition={{ duration: 0.2 }}
                      style={{ color: '#FF0066', fontSize: '24px', fontWeight: '300', flexShrink: 0, marginLeft: '16px' }}>+</motion.span>
                  </div>
                  <motion.div initial={false}
                    animate={{ height: openFaq === i ? 'auto' : 0, opacity: openFaq === i ? 1 : 0 }}
                    transition={{ duration: 0.3 }} style={{ overflow: 'hidden' }}>
                    <p style={{ color: '#777', fontSize: '14px', lineHeight: 1.8, margin: '0 0 24px' }}>{faq.a}</p>
                  </motion.div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: '#FF0066', padding: '80px 24px', textAlign: 'center' }}>
        <FadeUp>
          <motion.button onClick={() => navigate('/editeur')}
            whileHover={{ scale: 1.04, backgroundColor: '#1A1A1A' }} whileTap={{ scale: 0.97 }}
            style={{ backgroundColor: '#fff', color: '#FF0066', border: 'none', padding: '18px 48px', fontSize: '13px', letterSpacing: '3px', fontWeight: '900', cursor: 'pointer' }}>
            {d.cta}
          </motion.button>
        </FadeUp>
      </section>

      <Footer siteLang={siteLang} />
    </div>
  );
}